import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Users, FileText, Mail, Plus, ArrowRight, Building2, Heart, TrendingDown, BarChart3, MousePointerClick, UserCheck, Calculator } from 'lucide-react';
import { dashboardAPI, clientsAPI, analyticsAPI } from '@/lib/api';
import { SchemeCountdown } from '@/components/SchemeCountdown';

export default function AdminDashboard() {
  const [stats, setStats] = useState({
    total_clients: 0,
    micro_clients: 0,
    nonprofit_clients: 0,
    total_documents: 0,
    unread_contacts: 0,
    partner_worksheets: 0,
  });
  const [recentClients, setRecentClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [funnel, setFunnel] = useState(null);
  const [funnelPeriod, setFunnelPeriod] = useState('all_time');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, clientsRes, funnelRes] = await Promise.all([
          dashboardAPI.getStats(),
          clientsAPI.getAll(),
          analyticsAPI.getFunnel(),
        ]);
        setStats(statsRes.data);
        setRecentClients(clientsRes.data.slice(0, 5));
        setFunnel(funnelRes.data);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const statCards = [
    { title: 'Total Clients', value: stats.total_clients, icon: Users, color: 'bg-blue-500' },
    { title: 'Micro Businesses', value: stats.micro_clients, icon: Building2, color: 'bg-[#E84C3D]' },
    { title: 'Non-Profits', value: stats.nonprofit_clients, icon: Heart, color: 'bg-[#059669]' },
    { title: 'Documents', value: stats.total_documents, icon: FileText, color: 'bg-purple-500' },
    { title: 'New Inquiries', value: stats.unread_contacts, icon: Mail, color: 'bg-amber-500' },
    { title: 'Partner Assessments', value: stats.partner_worksheets, icon: Calculator, color: 'bg-teal-500', link: '/admin/partner-requests' },
  ];

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-screen">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8" data-testid="admin-dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Dashboard</h1>
          <p className="text-slate-600">Overview of your client vault</p>
        </div>
        <Link to="/admin/clients/new">
          <Button className="bg-[#E84C3D] hover:bg-[#C0392B]" data-testid="add-client-btn">
            <Plus size={18} className="mr-2" />
            Add New Client
          </Button>
        </Link>
      </div>

      {/* Scheme Countdown */}
      <div className="mb-6">
        <SchemeCountdown variant="admin" />
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4 mb-8">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          const inner = (
            <Card key={index} className={`stat-card ${stat.link ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`} data-testid={`stat-card-${index}`}>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-xl ${stat.color} flex items-center justify-center`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{stat.value}</p>
                    <p className="text-sm text-slate-500">{stat.title}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
          return stat.link ? <Link key={index} to={stat.link}>{inner}</Link> : <div key={index}>{inner}</div>;
        })}
      </div>

      {/* Calculator Funnel */}
      {funnel && (
        <Card className="mb-8" data-testid="funnel-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <div className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-[#E84C3D]" />
              <CardTitle className="text-lg">Calculator Funnel</CardTitle>
            </div>
            <div className="flex gap-1">
              {[
                { key: 'today', label: 'Today' },
                { key: 'this_week', label: 'This Week' },
                { key: 'this_month', label: 'This Month' },
                { key: 'all_time', label: 'All Time' },
              ].map(p => (
                <button
                  key={p.key}
                  onClick={() => setFunnelPeriod(p.key)}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-all ${funnelPeriod === p.key ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-500 border-slate-200 hover:border-slate-400'}`}
                  data-testid={`funnel-period-${p.key}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </CardHeader>
          <CardContent>
            {(() => {
              const d = funnel[funnelPeriod] || { started: 0, completed: 0, submitted: 0, soft_leads: 0, start_to_complete_pct: 0, complete_to_submit_pct: 0 };
              return (
                <div className="space-y-4">
                  {/* Funnel Steps */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-4" data-testid="funnel-started">
                      <div className="flex items-center gap-2 mb-2">
                        <MousePointerClick className="w-4 h-4 text-blue-600" />
                        <p className="text-xs text-blue-600 font-semibold uppercase">Started</p>
                      </div>
                      <p className="text-2xl font-bold text-blue-800">{d.started}</p>
                      <p className="text-[10px] text-blue-500 mt-1">Clicked first option</p>
                    </div>
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="funnel-completed">
                      <div className="flex items-center gap-2 mb-2">
                        <BarChart3 className="w-4 h-4 text-amber-600" />
                        <p className="text-xs text-amber-600 font-semibold uppercase">Completed</p>
                      </div>
                      <p className="text-2xl font-bold text-amber-800">{d.completed}</p>
                      <p className="text-[10px] text-amber-500 mt-1">{d.start_to_complete_pct}% of started</p>
                    </div>
                    <div className="bg-green-50 border border-green-200 rounded-xl p-4" data-testid="funnel-submitted">
                      <div className="flex items-center gap-2 mb-2">
                        <UserCheck className="w-4 h-4 text-green-600" />
                        <p className="text-xs text-green-600 font-semibold uppercase">Leads</p>
                      </div>
                      <p className="text-2xl font-bold text-green-800">{d.submitted}</p>
                      <p className="text-[10px] text-green-500 mt-1">{d.complete_to_submit_pct}% of completed</p>
                    </div>
                    <div className="bg-purple-50 border border-purple-200 rounded-xl p-4" data-testid="funnel-soft">
                      <div className="flex items-center gap-2 mb-2">
                        <TrendingDown className="w-4 h-4 text-purple-600" />
                        <p className="text-xs text-purple-600 font-semibold uppercase">Saved for Later</p>
                      </div>
                      <p className="text-2xl font-bold text-purple-800">{d.soft_leads}</p>
                      <p className="text-[10px] text-purple-500 mt-1">Drop-off recapture</p>
                    </div>
                  </div>

                  {/* Visual Funnel Bar */}
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-3">
                      <span className="text-[10px] text-slate-500 w-20 text-right">Started</span>
                      <div className="flex-1 bg-slate-100 rounded-full h-3 overflow-hidden">
                        <div className="bg-blue-500 h-full rounded-full transition-all" style={{ width: '100%' }} />
                      </div>
                      <span className="text-xs font-semibold text-slate-700 w-10">{d.started}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-[10px] text-slate-500 w-20 text-right">Completed</span>
                      <div className="flex-1 bg-slate-100 rounded-full h-3 overflow-hidden">
                        <div className="bg-amber-500 h-full rounded-full transition-all" style={{ width: `${d.start_to_complete_pct}%` }} />
                      </div>
                      <span className="text-xs font-semibold text-slate-700 w-10">{d.completed}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-[10px] text-slate-500 w-20 text-right">Leads</span>
                      <div className="flex-1 bg-slate-100 rounded-full h-3 overflow-hidden">
                        <div className="bg-green-500 h-full rounded-full transition-all" style={{ width: `${d.started > 0 ? (d.submitted / d.started * 100) : 0}%` }} />
                      </div>
                      <span className="text-xs font-semibold text-slate-700 w-10">{d.submitted}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-[10px] text-slate-500 w-20 text-right">Saved</span>
                      <div className="flex-1 bg-slate-100 rounded-full h-3 overflow-hidden">
                        <div className="bg-purple-500 h-full rounded-full transition-all" style={{ width: `${d.started > 0 ? (d.soft_leads / d.started * 100) : 0}%` }} />
                      </div>
                      <span className="text-xs font-semibold text-slate-700 w-10">{d.soft_leads}</span>
                    </div>
                  </div>

                  {/* Drop-off insight */}
                  {d.completed > 0 && d.submitted === 0 && d.soft_leads === 0 && (
                    <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                      {d.completed} user{d.completed !== 1 ? 's' : ''} saw savings but didn't convert yet. The "Save for Later" soft CTA should help recapture these.
                    </p>
                  )}
                </div>
              );
            })()}
          </CardContent>
        </Card>
      )}

      {/* Quick Actions & Recent Clients */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Quick Actions */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-lg">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Link to="/admin/clients/new" className="block">
              <Button variant="outline" className="w-full justify-start">
                <Plus size={18} className="mr-2" />
                Add New Client
              </Button>
            </Link>
            <Link to="/admin/clients" className="block">
              <Button variant="outline" className="w-full justify-start">
                <Users size={18} className="mr-2" />
                View All Clients
              </Button>
            </Link>
            <Link to="/admin/contacts" className="block">
              <Button variant="outline" className="w-full justify-start">
                <Mail size={18} className="mr-2" />
                View Inquiries
                {stats.unread_contacts > 0 && (
                  <span className="ml-auto bg-[#E84C3D] text-white text-xs px-2 py-0.5 rounded-full">
                    {stats.unread_contacts}
                  </span>
                )}
              </Button>
            </Link>
          </CardContent>
        </Card>

        {/* Recent Clients */}
        <Card className="lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg">Recent Clients</CardTitle>
            <Link to="/admin/clients">
              <Button variant="ghost" size="sm" className="text-[#E84C3D]">
                View All <ArrowRight size={16} className="ml-1" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {recentClients.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Users className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>No clients yet</p>
                <Link to="/admin/clients/new">
                  <Button className="mt-4 bg-[#E84C3D] hover:bg-[#C0392B]" size="sm">
                    Add Your First Client
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-3">
                {recentClients.map((client) => (
                  <Link 
                    key={client.id} 
                    to={`/admin/clients/${client.id}`}
                    className="flex items-center justify-between p-4 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors"
                    data-testid={`recent-client-${client.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center border border-slate-200">
                        <Building2 className="w-5 h-5 text-slate-400" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{client.company_name}</p>
                        <p className="text-sm text-slate-500">{client.contact_person}</p>
                      </div>
                    </div>
                    <span className={`badge ${client.client_type === 'micro_business' ? 'badge-micro' : 'badge-nonprofit'}`}>
                      {client.client_type === 'micro_business' ? 'Micro Business' : 'Non-Profit'}
                    </span>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
